<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { api, extractError } from '@/api/client'
import { useUnreadStore } from '@/stores/unread'
import type { NewsItem } from '@/types'
import { formatDateTime } from '@/utils/format'

const items = ref<NewsItem[]>([])
const unreadStore = useUnreadStore()
const unread = computed(() => unreadStore.newsUnread)
const loading = ref(true)
const error = ref('')

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get<{
      success: boolean
      news: NewsItem[]
      unread_count: number
    }>('/api/user/news')
    if (data.success) {
      items.value = data.news || []
      unreadStore.setNewsUnread(data.unread_count ?? 0)
    }
  } catch (e) {
    error.value = extractError(e)
  } finally {
    loading.value = false
  }
}

async function markRead(item: NewsItem) {
  if (item.is_read) return
  try {
    await api.post('/api/user/news', { action: 'mark_read', news_id: item.id })
    item.is_read = true
    unreadStore.decNewsUnread()
  } catch {
    /* ignore */
  }
}

async function markAllRead() {
  try {
    await api.post('/api/user/news', { action: 'mark_all_read' })
    items.value.forEach((n) => { n.is_read = true })
    unreadStore.setNewsUnread(0)
  } catch (e) {
    error.value = extractError(e)
  }
}

onMounted(() => void load())
</script>

<template>
  <div>
    <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h1 class="page-title">
        Новости
        <span v-if="unread > 0" class="ml-2 badge badge-danger">{{ unread }}</span>
      </h1>
      <button
        v-if="unread > 0"
        type="button"
        class="btn btn-sm btn-secondary"
        @click="markAllRead"
      >
        Прочитать все
      </button>
    </div>

    <div v-if="error" class="alert alert-danger mb-4">{{ error }}</div>
    <div v-if="loading" class="panel text-muted">Загрузка…</div>
    <div v-else-if="items.length === 0" class="panel text-muted">Новостей пока нет.</div>

    <div v-else class="space-y-3">
      <article
        v-for="item in items"
        :key="item.id"
        class="panel cursor-pointer !p-4 transition hover:shadow-md"
        :class="!item.is_read ? 'border-l-4 border-l-primary bg-primary-light/20' : ''"
        @click="markRead(item)"
      >
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
          <span>{{ formatDateTime(item.created_at) }}</span>
          <span v-if="item.server_name">Сервер: {{ item.server_name }}</span>
          <span v-if="!item.is_read" class="badge badge-outline-primary">новое</span>
        </div>
        <div class="whitespace-pre-wrap text-sm" dir="auto">{{ item.content }}</div>
      </article>
    </div>
  </div>
</template>
